/**
 * UserAnnotation runtime hooks.
 *
 * The annotation render path is *nested* — every `BlockViewContainer`
 * (and the modal) mounts its own `AnnotationViewContainer`, which
 * subscribes per-block via `useByBlock(blockId)`. That nesting is
 * load-bearing: each layer's coordinate frame is its host's CSS rect
 * (lipped shrink, modal FLIP grow, `mix-blend-mode: color` stacking
 * context all lean on it).
 *
 * The cross-cutting bookkeeping that *isn't* per-block — auth-cache
 * clearing on user-id transitions, batched warming for the visible
 * block set — lives here as two root-level hooks.
 *
 * `useAnnotations()` mounts once at the app root inside the auth-aware
 * subtree and clears the local TanStack DB collection whenever the
 * authenticated user changes. Replaces the wiring that used to live
 * in `auth-context.tsx`.
 *
 * `useAnnotationsForBlocks(blockIds)` is called wherever the visible
 * block-id set is computed (today the slate's `useList` effect). It
 * batches a fetch through `userAnnotationStore.ensureLoadedForBlocks`
 * with a stable string key so the effect only re-runs when the id set
 * genuinely changes. Replaces the warming wiring that used to live
 * in `block-store.ts` (the `insertEnvelopes` follow-up call and the
 * `useList`'s settled-blocks `useEffect`).
 */
import { useEffect, useRef } from "react";
import { userAnnotationStore } from "@/data/user-annotation-store";
import { useAuth } from "@/auth/auth-context";
/**
 * Mount once at the app root (inside the auth-aware subtree so
 * `useAuth()` resolves). Watches `user.id` and drops every locally-
 * cached annotation row when the identity changes — sign-out, sign-in
 * as a different user — so optimistic placeholders or stale rows from
 * the prior session don't bleed into the next one.
 *
 * Returns `void` deliberately: this is a runtime side-effect hook,
 * not a data-source hook. Adding a return value later is additive.
 */
export function useAnnotations() {
    const { user } = useAuth();
    const userId = user?.id;
    const previousUserIdRef = useRef(userId);
    useEffect(() => {
        const prev = previousUserIdRef.current;
        previousUserIdRef.current = userId;
        if (prev !== userId) {
            userAnnotationStore.clearAllCaches();
        }
    }, [userId]);
}
/**
 * Warm the local annotation collection for `blockIds`. Dedupes
 * per-block under the hood (concurrent calls for the same id collapse
 * to one fetch), and skips any block already in the cache, so this is
 * cheap to call from any component that knows its visible block set.
 *
 * The effect's dep array is a stable join-key rather than the array
 * reference, so re-renders that hand back a fresh `[ids]` array but
 * the same contents don't re-trigger the warming.
 */
export function useAnnotationsForBlocks(blockIds) {
    // Stable primitive key: depending on the array reference would
    // re-fire the effect on every parent render even when the contents
    // haven't moved.
    const idsKey = blockIds.join("|");
    useEffect(() => {
        if (blockIds.length === 0)
            return;
        void userAnnotationStore.ensureLoadedForBlocks([...blockIds]);
        // `idsKey` collapses the deep list into a primitive React can diff
        // cheaply; we intentionally depend on it instead of `blockIds`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey]);
}
